import React, {
  useEffect,
  useState,
} from "react";

import styles from "./BankVari.module.css";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

/* =========================================================
   API
========================================================= */

const API_URL =
  "https://coinyoco.world/brobizee-api/partnerdata.php";

/* =========================================================
   COMPONENT
========================================================= */

export default function BankVari({
  onBack,
  onSubmit,
}) {
  const navigate = useNavigate();

  const {
    userId,
    deviceUuid,
  } = useParams();

  /* =======================================================
     URL
  ======================================================= */

  const basePath =
    userId && deviceUuid
      ? `/${userId}/${deviceUuid}`
      : "";

  const goTo = (path) => {
    navigate(
      basePath
        ? `${basePath}${path}`
        : path
    );
  };

  /* =======================================================
     STORAGE KEY
  ======================================================= */

  const STORAGE_KEY =
    userId && deviceUuid
      ? `partnerProfile_${userId}_${deviceUuid}`
      : "partnerProfile";

  /* =======================================================
     FORM STATE
  ======================================================= */

  const [
    accountType,
    setAccountType,
  ] = useState("Savings");

  const [
    nameOnAccount,
    setNameOnAccount,
  ] = useState("");

  const [
    accountNumber,
    setAccountNumber,
  ] = useState("");

  const [
    ifscCode,
    setIfscCode,
  ] = useState("");

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  /* =======================================================
     RESTORE EXISTING BANK DATA
  ======================================================= */

  useEffect(() => {
    try {
      const saved =
        localStorage.getItem(
          STORAGE_KEY
        );

      if (!saved) {
        return;
      }

      const data =
        JSON.parse(saved);

      if (!data) {
        return;
      }

      /* ACCOUNT TYPE */

      setAccountType(
        data.account_type ||
        "Savings"
      );

      /* ACCOUNT HOLDER */

      setNameOnAccount(
        data.account_holder_name ||
        ""
      );

      /* ACCOUNT NUMBER */

      setAccountNumber(
        data.account_number ||
        ""
      );

      /* IFSC */

      setIfscCode(
        data.ifsc_code ||
        ""
      );
    } catch (error) {
      console.error(
        "Unable to restore bank data:",
        error
      );
    }
  }, [STORAGE_KEY]);

  /* =======================================================
     ACCOUNT NUMBER
  ======================================================= */

  const handleAccountNumberChange = (
    e
  ) => {
    const value =
      e.target.value
        .replace(/\D/g, "")
        .slice(0, 18);

    setAccountNumber(value);
  };

  /* =======================================================
     IFSC CODE
  ======================================================= */

  const handleIfscChange = (
    e
  ) => {
    const value =
      e.target.value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 11);

    setIfscCode(value);
  };

  /* =======================================================
     FORM VALIDATION
  ======================================================= */

  const isFormValid =
    accountType.trim().length > 0 &&
    nameOnAccount.trim().length >= 2 &&
    accountNumber.length >= 9 &&
    ifscCode.length === 11;

  /* =======================================================
     GET PREVIOUS DATA
  ======================================================= */

  const getPreviousData = () => {
    try {
      const saved =
        localStorage.getItem(
          STORAGE_KEY
        );

      if (!saved) {
        return {};
      }

      return (
        JSON.parse(saved) || {}
      );
    } catch (error) {
      console.error(
        "Unable to read previous partner data:",
        error
      );

      return {};
    }
  };

  /* =======================================================
     SAVE BANK DATA
     
     IMPORTANT:
     Existing data is preserved.
     ONLY bank fields are updated.
  ======================================================= */

  const saveBankData =
    async () => {
      const previousData =
        getPreviousData();

      /* =================================================
         MERGE EXISTING DATA
         
         DO NOT create empty values for old fields.
         This prevents previous page data from disappearing.
      ================================================== */

      const dataToSave = {
        ...previousData,

        /* ===============================================
           IDENTIFICATION
        ================================================ */

        secondary_id:
          userId ||
          previousData.secondary_id ||
          "",

        secondary_uuid:
          deviceUuid ||
          previousData.secondary_uuid ||
          "",

        /* ===============================================
           BANK DATA
           
           ONLY THESE FOUR FIELDS ARE UPDATED.
        ================================================ */

        account_type:
          accountType,

        account_holder_name:
          nameOnAccount.trim(),

        account_number:
          accountNumber,

        ifsc_code:
          ifscCode,
      };

      console.log(
        "FINAL PARTNER DATA:",
        dataToSave
      );

      /* =================================================
         API REQUEST
      ================================================== */

      const response =
        await fetch(
          API_URL,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body:
              JSON.stringify(
                dataToSave
              ),
          }
        );

      const responseText =
        await response.text();

      console.log(
        "Bank API response:",
        responseText
      );

      /* =================================================
         PARSE RESPONSE
      ================================================== */

      let result;

      try {
        result =
          JSON.parse(
            responseText
          );
      } catch (error) {
        throw new Error(
          "PHP API returned invalid JSON."
        );
      }

      /* =================================================
         HTTP ERROR
      ================================================== */

      if (!response.ok) {
        throw new Error(
          result.message ||
          `Server error: ${response.status}`
        );
      }

      /* =================================================
         API ERROR
      ================================================== */

      if (!result.success) {
        throw new Error(
          result.message ||
          "Bank data could not be saved."
        );
      }

      /* =================================================
         SAVE COMPLETE MERGED DATA LOCALLY
      ================================================== */

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          dataToSave
        )
      );

      /*
        Keep common storage synchronized too.
      */

      localStorage.setItem(
        "partnerProfile",
        JSON.stringify(
          dataToSave
        )
      );

      /* =================================================
         CALLBACK
      ================================================== */

      if (onSubmit) {
        await onSubmit({
          accountType,
          nameOnAccount:
            nameOnAccount.trim(),
          accountNumber,
          ifscCode,
        });
      }

      return result;
    };

  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      /* ACCOUNT TYPE */

      if (!accountType) {
        alert(
          "Please select account type."
        );

        return;
      }

      /* NAME */

      if (
        nameOnAccount.trim()
          .length < 2
      ) {
        alert(
          "Please enter a valid name."
        );

        return;
      }

      /* ACCOUNT NUMBER */

      if (
        accountNumber.length < 9
      ) {
        alert(
          "Please enter a valid account number."
        );

        return;
      }

      /* IFSC */

      if (
        ifscCode.length !== 11
      ) {
        alert(
          "Please enter a valid 11-character IFSC code."
        );

        return;
      }

      /* USER ID */

      if (!userId) {
        alert(
          "User ID is missing."
        );

        return;
      }

      /* DEVICE UUID */

      if (!deviceUuid) {
        alert(
          "Device UUID is missing."
        );

        return;
      }

      try {
        setIsSubmitting(true);

        await saveBankData();

        /* ===============================================
           FINAL PAGE
        ================================================ */

        goTo(
          "/varified_Page"
        );
      } catch (error) {
        console.error(
          "Bank verification failed:",
          error
        );

        alert(
          error.message ||
          "Unable to save bank information."
        );
      } finally {
        setIsSubmitting(false);
      }
    };

  /* =======================================================
     BACK
  ======================================================= */

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    navigate(-1);
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className={
        styles.screenWrapper
      }
    >
      <div
        className={
          styles.deviceFrame
        }
      >

        {/* =================================================
            AMBIENT BACKGROUND
        ================================================== */}

        <div
          className={`${styles.ambient} ${styles.ambientOne}`}
        />

        <div
          className={`${styles.ambient} ${styles.ambientTwo}`}
        />

        <div
          className={
            styles.scrollContainer
          }
        >

          {/* =================================================
              HEADER
          ================================================== */}

          <header
            className={
              styles.header
            }
          >

            <button
              type="button"
              className={
                styles.backButton
              }
              onClick={
                handleBack
              }
              aria-label="Go Back"
            >

              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>

            </button>

            {/* STEP INDICATOR */}

            <div
              className={
                styles.stepIndicator
              }
            >

              <span
                className={
                  styles.step
                }
              />

              <span
                className={
                  styles.step
                }
              />

              <span
                className={
                  styles.step
                }
              />

              <span
                className={`${styles.step} ${styles.stepActive}`}
              />

            </div>

          </header>

          {/* =================================================
              MAIN
          ================================================== */}

          <main
            className={
              styles.mainContent
            }
          >

            {/* BANK ICON */}

            <div
              className={
                styles.iconWrapper
              }
            >

              <div
                className={
                  styles.bankIcon
                }
              >

                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >

                  <path d="M3 10h18" />

                  <path d="M5 10v8" />
                  <path d="M9 10v8" />
                  <path d="M15 10v8" />
                  <path d="M19 10v8" />

                  <path d="M2 18h20" />

                  <path
                    d="M12 3l10 5H2l10-5z"
                  />

                </svg>

              </div>

            </div>

            {/* =================================================
                TITLE
            ================================================== */}

            <div
              className={
                styles.titleSection
              }
            >

              <span
                className={
                  styles.eyebrow
                }
              >
                SECURE VERIFICATION
              </span>

              <h1
                className={
                  styles.title
                }
              >
                Bank
                <span>
                  {" "}Verification
                </span>
              </h1>

              <p
                className={
                  styles.subtitle
                }
              >
                Add your bank details securely
                to continue with BroBizee.
              </p>

            </div>

            {/* =================================================
                FORM
            ================================================== */}

            <form
              onSubmit={
                handleSubmit
              }
              className={
                styles.formContainer
              }
            >

              {/* =================================================
                  ACCOUNT TYPE
              ================================================== */}

              <div
                className={
                  styles.sectionGroup
                }
              >

                <label
                  className={
                    styles.sectionLabel
                  }
                >
                  Account type
                </label>

                <div
                  className={
                    styles.accountTypeList
                  }
                >

                  {[
                    "Savings",
                    "Current",
                  ].map(
                    (type) => {

                      const isSelected =
                        accountType ===
                        type;

                      return (
                        <button
                          key={type}
                          type="button"
                          className={`${styles.typeCard} ${
                            isSelected
                              ? styles.typeCardSelected
                              : ""
                          }`}
                          onClick={() =>
                            setAccountType(
                              type
                            )
                          }
                        >

                          <div
                            className={
                              styles.typeContent
                            }
                          >

                            <div
                              className={
                                styles.typeIcon
                              }
                            >

                              <svg
                                width="19"
                                height="19"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >

                                <path d="M3 10h18" />
                                <path d="M5 10v8" />
                                <path d="M9 10v8" />
                                <path d="M15 10v8" />
                                <path d="M19 10v8" />
                                <path d="M2 18h20" />
                                <path d="M12 3l10 5H2l10-5z" />

                              </svg>

                            </div>

                            <span
                              className={
                                styles.typeLabel
                              }
                            >
                              {type}
                            </span>

                          </div>

                          <div
                            className={`${styles.radioButton} ${
                              isSelected
                                ? styles.radioSelected
                                : ""
                            }`}
                          >

                            {isSelected && (
                              <div
                                className={
                                  styles.radioInnerCircle
                                }
                              />
                            )}

                          </div>

                        </button>
                      );
                    }
                  )}

                </div>

              </div>

              {/* =================================================
                  NAME ON ACCOUNT
              ================================================== */}

              <div
                className={
                  styles.sectionGroup
                }
              >

                <label
                  htmlFor="account-name"
                  className={
                    styles.sectionLabel
                  }
                >
                  Name on Account
                </label>

                <div
                  className={
                    styles.inputWrapper
                  }
                >

                  <span
                    className={
                      styles.inputIcon
                    }
                  >

                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >

                      <circle
                        cx="12"
                        cy="8"
                        r="4"
                      />

                      <path
                        d="M4 21c0-4 3.5-7 8-7s8 3 8 7"
                      />

                    </svg>

                  </span>

                  <input
                    id="account-name"
                    type="text"
                    value={
                      nameOnAccount
                    }
                    onChange={(e) =>
                      setNameOnAccount(
                        e.target.value
                      )
                    }
                    placeholder="Enter full name"
                    className={
                      styles.inputField
                    }
                  />

                </div>

              </div>

              {/* =================================================
                  ACCOUNT NUMBER
              ================================================== */}

              <div
                className={
                  styles.sectionGroup
                }
              >

                <label
                  htmlFor="account-number"
                  className={
                    styles.sectionLabel
                  }
                >
                  Account Number
                </label>

                <div
                  className={
                    styles.inputWrapper
                  }
                >

                  <span
                    className={
                      styles.inputIcon
                    }
                  >

                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >

                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="14"
                        rx="2"
                      />

                      <path d="M7 10h4" />
                      <path d="M7 14h2" />
                      <path d="M14 10h3" />
                      <path d="M13 14h4" />

                    </svg>

                  </span>

                  <input
                    id="account-number"
                    type="tel"
                    inputMode="numeric"
                    value={
                      accountNumber
                    }
                    onChange={
                      handleAccountNumberChange
                    }
                    placeholder="Enter account number"
                    maxLength={18}
                    className={
                      styles.inputField
                    }
                  />

                </div>

                <div
                  className={
                    styles.helperText
                  }
                >

                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >

                    <rect
                      x="5"
                      y="11"
                      width="14"
                      height="10"
                      rx="2"
                    />

                    <path
                      d="M8 11V7a4 4 0 018 0v4"
                    />

                  </svg>

                  Your bank information is encrypted
                  and protected.

                </div>

              </div>

              {/* =================================================
                  IFSC CODE
              ================================================== */}

              <div
                className={
                  styles.sectionGroup
                }
              >

                <label
                  htmlFor="ifsc-code"
                  className={
                    styles.sectionLabel
                  }
                >
                  IFSC Code
                </label>

                <div
                  className={
                    styles.inputWrapper
                  }
                >

                  <span
                    className={
                      styles.inputIcon
                    }
                  >

                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >

                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="16"
                        rx="2"
                      />

                      <path d="M7 8h10" />

                      <path d="M7 12h4" />

                      <path d="M7 16h6" />

                    </svg>

                  </span>

                  <input
                    id="ifsc-code"
                    type="text"
                    value={
                      ifscCode
                    }
                    onChange={
                      handleIfscChange
                    }
                    placeholder="Enter 11-character IFSC"
                    maxLength={11}
                    autoCapitalize="characters"
                    autoComplete="off"
                    className={
                      styles.inputField
                    }
                  />

                </div>

                <div
                  className={
                    styles.helperText
                  }
                >

                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >

                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                    />

                    <path d="M12 8v4" />

                    <path d="M12 16h.01" />

                  </svg>

                  Enter your bank's 11-character IFSC
                  code.

                </div>

              </div>

              {/* =================================================
                  SUBMIT
              ================================================== */}

              <div
                className={
                  styles.actionWrapper
                }
              >

                <button
                  type="submit"
                  disabled={
                    !isFormValid ||
                    isSubmitting
                  }
                  className={`${styles.submitBtn} ${
                    isFormValid
                      ? styles.submitEnabled
                      : ""
                  }`}
                >

                  <span>
                    {isSubmitting
                      ? "Saving..."
                      : "Verify & Continue"}
                  </span>

                  <span
                    className={
                      styles.arrowCircle
                    }
                  >

                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >

                      <path d="M5 12h14" />

                      <path
                        d="M13 6l6 6-6 6"
                      />

                    </svg>

                  </span>

                </button>

              </div>

            </form>

          </main>

          {/* =================================================
              FOOTER
          ================================================== */}

          <footer
            className={
              styles.footer
            }
          >

            <img
              src="/images/logo.png"
              alt="BroBizee Logo"
              className={
                styles.brandLogo
              }
            />

            <span
              className={
                styles.footerText
              }
            >
              Secure. Simple. BroBizee.
            </span>

          </footer>

        </div>

      </div>

    </div>
  );
}